import { inject, Injectable, WritableSignal } from '@angular/core';
import { WebSocketService } from './web-socket.service';
import { AuthService } from './auth.service';
import { TopicType } from '../enums/topic-type';
import { CodeData } from '@interfaces/code-team/code-data';
import { TeamMemberResponse } from '@interfaces/code-team/team-member.response';
import { TeamMember } from '@interfaces/code-team/team-member';
import { CodeTeamStore } from '@stores/code-team.store';
import { CodeType } from '../enums/code-type';
import { DataService } from '@services/data.service';
import { DataCategoryType } from '../enums/data-category-type';
import { StaffService } from '@services/staff.service';
import { DataArgument } from '@interfaces/data/data-argument';

enum CodeTeamMethod {
  CODE = 'code',
  USER = 'user',
}

@Injectable({
  providedIn: 'root',
})
export class CodeTeamService {
  private codeTeamStore = inject(CodeTeamStore);
  private dataService = inject(DataService);
  private staffService = inject(StaffService);
  teamMembers = this.staffService.teamMembers as WritableSignal<TeamMember[]>;
  users = this.codeTeamStore.teamMembers as WritableSignal<TeamMember[]>;
  codes = this.codeTeamStore.codes as WritableSignal<CodeData[]>;

  constructor(
    private authService: AuthService,
    private webSocketService: WebSocketService,
  ) {}

  updateCodes(codeData: CodeData[]) {
    this.codeTeamStore.updateCodes(codeData);
  }

  addTeamMember(newMember: TeamMember) {
    const currentMembers = this.teamMembers();
    this.teamMembers.set([...currentMembers, newMember]);
  }

  setTeamMembers(res: TeamMemberResponse) {
    this.codeTeamStore.updateTeamMembers(res.users);
  }

  sendCodeRequest(): void {
    const userId = this.authService.user().id;
    const message: object = {
      requester: userId,
    };

    this.webSocketService.send({ topic: TopicType.CODE_DATA_REQUEST, message });
  }

  sendStaffDataRequest(
    args: DataArgument[] = [
      {
        category: DataCategoryType.STAFF,
        filters: {
          permission: 'maEmergencyAlarm',
        },
      },
    ],
  ): void {
    this.dataService.sendDataRequest(args);
  }

  sendTeamMemberRequest() {
    const userId = this.authService.user().id;
    const message: object = {
      requester: userId,
    };
    this.webSocketService.send({ topic: TopicType.GET_USER_REQUEST, message });
  }

  sendMemberRequest(teamMember: TeamMember, codes: CodeData[]) {
    const userId = this.authService.user().id;
    const message: object = {
      requester: userId,
      method: CodeTeamMethod.USER,
      target: teamMember.id,
      data: codes.map((tm) => tm.name) ?? [],
    };
    this.webSocketService.send({
      topic: TopicType.CODE_UPDATE_REQUEST,
      message,
    });
    this.sendCodeRequest();
    this.sendStaffDataRequest();
  }

  sendCodeTeamRequest(target: CodeType, teamMembers: TeamMember[]) {
    const userId = this.authService.user().id;
    const message: object = {
      requester: userId,
      method: CodeTeamMethod.CODE,
      target: target,
      data: teamMembers.map((tm) => tm.id) ?? [],
    };
    this.webSocketService.send({
      topic: TopicType.CODE_UPDATE_REQUEST,
      message,
    });
    this.sendCodeRequest();
    this.sendStaffDataRequest();
  }
}
